import { useState } from "preact/hooks";
import type { ProgressBarColor, Setting, Shipment, Texts } from "@/type";
import {
  formatTimestamp,
  getShipmentStatusDesc,
  getTrackStatusKey,
  isTrackingNotAdded,
} from "@/utils/tracking";
import PendingIllustration from "./PendingIllustration";

const DEFAULT_VISIBLE_MIDDLE = 2;

interface TrackingTimelineProps {
  settings: Setting;
  texts: Texts;
  shipment: Shipment;
}

export default function TrackingTimeline({ settings, texts, shipment }: TrackingTimelineProps) {
  const { style, font_family } = settings;
  const { colors, general_content_text_size: typography, borders } = style;
  const { text_color } = colors;
  const { shipping_status_text, shipping_status_text_tip, timer_text } = text_color;
  const [expanded, setExpanded] = useState(false);

  const events = shipment.events || [];
  const statusColorKey = getTrackStatusKey(shipment.track_status ?? 0);
  const isPending = statusColorKey === "ordered";
  const progressColor = colors.progress_bar_color[statusColorKey as keyof ProgressBarColor];
  const progressPercent = shipment.progress_percent ?? 5;
  const statusLabel = shipment.status_label || "";
  const statusDesc = getShipmentStatusDesc(texts, shipment.track_status ?? 0, shipment.status_desc);
  const latestEvent = events[0];

  if (isTrackingNotAdded(shipment)) {
    return (
      <div className="flex flex-col min-h-[360px]" style={{ fontFamily: font_family.font }}>
        <div className="flex flex-col w-full" style={{ gap: 8 }}>
          <p
            style={{
              color: shipping_status_text,
              fontSize: typography.current_order_status,
              fontWeight: 650,
              lineHeight: "20px",
              margin: 0,
            }}
          >
            {texts.tracking_not_added_status}
          </p>
          <p
            style={{
              color: text_color.shipping_status_text_tip,
              fontSize: typography.shipping_status_text_tip,
              fontWeight: 450,
              lineHeight: "20px",
              margin: 0,
            }}
          >
            {texts.tracking_not_added_status_desc}
          </p>
        </div>
        <div
          className="flex flex-1 items-center justify-center"
          style={{ paddingTop: 48, paddingBottom: 48 }}
        >
          <PendingIllustration />
        </div>
      </div>
    );
  }

  if (!latestEvent) return null;

  const middleEvents = events.slice(1, events.length - 1);
  const lastEvent = events[events.length - 1];
  const hasLastEvent = events.length > 1;
  const hasMore = middleEvents.length > DEFAULT_VISIBLE_MIDDLE;
  const visibleMiddle = expanded ? middleEvents : middleEvents.slice(0, DEFAULT_VISIBLE_MIDDLE);
  const lineAfterSeeAll = hasLastEvent;
  const totalCount = shipment.total_events ?? events.length;

  return (
    <div style={{ fontFamily: font_family.font }}>
      {/* 状态标题 */}
      <div className="flex flex-col" style={{ gap: 4, marginBottom: 8 }}>
        <div className="flex items-baseline flex-wrap" style={{ gap: 4 }}>
          <span
            style={{
              color: shipping_status_text,
              fontSize: typography.current_order_status,
              fontWeight: 650,
              lineHeight: "20px",
            }}
          >
            {statusLabel}
          </span>
          <span
            style={{
              color: timer_text,
              fontSize: typography.timer_text,
              fontWeight: 450,
              lineHeight: "20px",
            }}
          >
            · {formatTimestamp(latestEvent.time)}
          </span>
        </div>
        {statusDesc && (
          <p
            style={{
              color: shipping_status_text_tip,
              fontSize: typography.shipping_status_text_tip,
              fontWeight: 450,
              lineHeight: "20px",
            }}
          >
            {statusDesc}
          </p>
        )}
      </div>

      {/* 进度条 */}
      <div
        className="w-full overflow-hidden"
        style={{
          height: 8,
          backgroundColor: "#e3e3e3",
          borderRadius: borders.progress_bar_corner_radius,
          marginBottom: 20,
        }}
      >
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${progressPercent}%`,
            backgroundColor: progressColor,
            borderRadius: borders.progress_bar_corner_radius,
          }}
        />
      </div>

      {/* 时间线列表 */}
      <div>
        <TimelineRow
          icon={isPending ? "dot" : "pin"}
          statusLabel={latestEvent.status_label || ""}
          time={formatTimestamp(latestEvent.time)}
          location={isPending ? "" : latestEvent.location || ""}
          description={isPending ? "" : latestEvent.description || ""}
          colors={colors}
          typography={typography}
          hasLine={isPending || visibleMiddle.length > 0 || hasMore || hasLastEvent}
          isPending={isPending}
        />

        {visibleMiddle.map((event, idx) => {
          const isLast = idx === visibleMiddle.length - 1;
          return (
            <TimelineRow
              key={idx}
              icon="dot"
              statusLabel={event.status_label || ""}
              time={formatTimestamp(event.time)}
              location={event.location || ""}
              description={event.description || ""}
              colors={colors}
              typography={typography}
              hasLine={!isLast || hasMore || hasLastEvent}
            />
          );
        })}

        {hasMore && (
          <div className="flex" style={{ gap: 16 }}>
            <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
              <div className="flex-shrink-0" style={{ marginTop: 2 }}>
                <DotIcon color={colors.general_color.timeline} />
              </div>
              {lineAfterSeeAll && (
                <div
                  className="flex-1 mt-2"
                  style={{ borderLeft: `1.5px dashed ${colors.general_color.timeline}` }}
                />
              )}
            </div>
            <div className="pb-5">
              <button
                style={{
                  color: shipping_status_text,
                  fontSize: typography.shipping_status_text_tip,
                  fontWeight: 450,
                  lineHeight: "20px",
                }}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? "Collapse" : `${texts.time_line_text} (${totalCount})`}
              </button>
            </div>
          </div>
        )}

        {hasLastEvent && (
          <TimelineRow
            icon="dot"
            statusLabel={lastEvent.status_label || ""}
            time={formatTimestamp(lastEvent.time)}
            location={lastEvent.location || ""}
            description={lastEvent.description || ""}
            colors={colors}
            typography={typography}
            hasLine={false}
          />
        )}
      </div>

      {isPending && (
        <div
          className="flex items-center justify-center"
          style={{ paddingTop: 24, paddingBottom: 24 }}
        >
          <PendingIllustration />
        </div>
      )}
    </div>
  );
}

interface TimelineRowProps {
  icon: "pin" | "dot";
  statusLabel: string;
  time: string;
  location: string;
  description: string;
  colors: Setting["style"]["colors"];
  typography: Setting["style"]["general_content_text_size"];
  hasLine: boolean;
  isPending?: boolean;
}

function TimelineRow({
  icon,
  statusLabel,
  time,
  location,
  description,
  colors,
  typography,
  hasLine,
  isPending,
}: TimelineRowProps) {
  const { shipping_status_text, shipping_status_text_tip, timer_text } = colors.text_color;
  const timelineColor = colors.general_color.timeline;

  const tipStyle = {
    color: shipping_status_text_tip,
    fontSize: typography.shipping_status_text_tip,
    fontWeight: 450,
    lineHeight: "20px",
  };

  return (
    <div className="flex min-h-[76px] gap-4">
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
        <div className="flex-shrink-0" style={{ marginTop: icon === "pin" ? 0 : 6 }}>
          {icon === "pin" ? (
            <LocationIcon color={timelineColor} />
          ) : (
            <DotIcon color={timelineColor} />
          )}
        </div>
        {hasLine && (
          <div className="flex-1 mt-2" style={{ borderLeft: `1.5px dashed ${timelineColor}` }} />
        )}
      </div>

      <div className="flex-1 min-w-0" style={{ paddingBottom: isPending ? 0 : 20 }}>
        <div className="flex items-baseline flex-wrap" style={{ gap: 4 }}>
          <span
            style={{
              color: shipping_status_text,
              fontSize: typography.shipping_status_text,
              fontWeight: 550,
              lineHeight: "20px",
            }}
          >
            {statusLabel}
          </span>
          <span
            style={{
              color: timer_text,
              fontSize: typography.timer_text,
              fontWeight: 450,
              lineHeight: "20px",
            }}
          >
            · {time}
          </span>
        </div>
        {(location || description) && (
          <div className="flex flex-col" style={{ gap: 4, marginTop: 8 }}>
            {location && <p style={{ ...tipStyle, margin: 0 }}>{location}</p>}
            {description && <p style={{ ...tipStyle, margin: 0 }}>{description}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function LocationIcon({ color }: { color: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10 11C11.3807 11 12.5 9.88071 12.5 8.5C12.5 7.11929 11.3807 6 10 6C8.61929 6 7.5 7.11929 7.5 8.5C7.5 9.88071 8.61929 11 10 11ZM10 9.5C10.5523 9.5 11 9.05228 11 8.5C11 7.94772 10.5523 7.5 10 7.5C9.44772 7.5 9 7.94772 9 8.5C9 9.05228 9.44772 9.5 10 9.5Z"
        fill={color}
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.82655 16H5.75C5.33579 16 5 16.3358 5 16.75C5 17.1642 5.33579 17.5 5.75 17.5H14.25C14.6642 17.5 15 17.1642 15 16.75C15 16.3358 14.6642 16 14.25 16H11.1734C11.196 15.98 11.219 15.9596 11.2424 15.9387C11.707 15.5229 12.3273 14.9219 12.9495 14.1805C14.1737 12.7217 15.5 10.6071 15.5 8.2267C15.5 5.06038 13.1721 2.75 10 2.75C6.82792 2.75 4.5 5.06038 4.5 8.2267C4.5 10.6071 5.82627 12.7217 7.0505 14.1805C7.6727 14.9219 8.29304 15.5229 8.75765 15.9387C8.781 15.9596 8.80398 15.98 8.82655 16ZM10 4.25C7.64924 4.25 6 5.8959 6 8.2267C6 10.073 7.04873 11.845 8.1995 13.2162C8.7648 13.8898 9.33196 14.4397 9.75798 14.8209C9.84521 14.899 9.9263 14.9698 10 15.033C10.0737 14.9698 10.1548 14.899 10.242 14.8209C10.668 14.4397 11.2352 13.8898 11.8005 13.2162C12.9513 11.845 14 10.073 14 8.2267C14 5.8959 12.3508 4.25 10 4.25Z"
        fill={color}
      />
    </svg>
  );
}

function DotIcon({ color }: { color: string }) {
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: 3,
        backgroundColor: color,
        border: `1.25px solid ${color}`,
      }}
    />
  );
}
